import { CreditCardOutlined,FilterOutlined,PlusOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { Button,Flex,Grid,Switch,Tag,Typography } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import type { StudioWithAccess } from "../../../app/api/types";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateList,formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { resolveArticleSaleDisplayStatus } from "../../../app/utils/articleSaleStatus";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import { ArticleSaleEditDrawer } from "../../../components/ArticleSaleEditDrawer";
import { FormDrawer } from "../../../components/FormDrawer";
import { PaymentDrawer,type PaymentItem } from "../../../components/PaymentDrawer";
import { SettingsListPage,type SettingsListActionRef } from "../../../components/SettingsListPage";
import type { PersonDto } from "../types";
import { PersonArticleSaleDrawer } from "./PersonArticleSaleDrawer";
import { parseDateOnly } from "./personDetailUtils";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { ARTICLE_SALE_STATUS_CANCELLED } from "../../../app/constants/articleSaleStatus";

const OPEN_AMOUNT_ROUNDING_TOLERANCE = 0.005;
const SALE_DATE_COLUMN_WIDTH = 120;
const TOTAL_GROSS_AMOUNT_COLUMN_WIDTH = 140;
const PAID_AMOUNT_COLUMN_WIDTH = 140;
const OPEN_AMOUNT_COLUMN_WIDTH = 140;
const PAYMENT_METHOD_COLUMN_WIDTH = 180;
const PAYMENT_DATES_COLUMN_WIDTH = 160;
const STATUS_COLUMN_WIDTH = 120;
const RECEIPT_NUMBER_COLUMN_WIDTH = 160;

type ArticleSaleListItem = {
  id: string;
  personId: string;
  personName: string;
  saleDate: string;
  status: number;
  sourceType: number;
  totalGrossAmount: number;
  paidAmount?: number;
  openAmount?: number;
  openItemId?: string | null;
  submittedSepaDirectDebitRunId?: string | null;
  isPaymentBlockedByActiveSepaRun: boolean;
  paymentMethod?: number | null;
  sepaGroupId?: string | null;
  receiptNumber?: string | null;
  studioId?: string | null;
  articleNames?: string;
  paymentDates?: string[];
  canCancel: boolean;
};

type ArticleSalesListResponse = { items: ArticleSaleListItem[]; totalCount: number };

type Props = {
  api: Pick<ApiClient, "get" | "post" | "put" | "req">;
  message: Feedback;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  homeStudioId?: string | null;
  allowedStudios: StudioWithAccess[];
  dateInputFormat: string | string[];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  paymentMethodNameByType: Record<number, string>;
  canRead: boolean;
  canVoid: boolean;
  canPay: boolean;
  showHistoricalArticleSales: boolean;
  setShowHistoricalArticleSales: (value: boolean) => void;
  onPersonUpdated: (person: PersonDto) => void;
  onPaymentRecorded: () => void;
};

export function PersonArticleSalesTab({
  api,
  message,
  text,
  units,
  personId,
  homeStudioId,
  allowedStudios,
  dateInputFormat,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  paymentMethodNameByType,
  canRead,
  canVoid,
  canPay,
  showHistoricalArticleSales,
  setShowHistoricalArticleSales,
  onPersonUpdated,
  onPaymentRecorded
}: Props) {
  const actionRef = React.useRef<SettingsListActionRef>(null);
  const [sellDrawerOpen, setSellDrawerOpen] = React.useState(false);
  const [paymentTarget, setPaymentTarget] = React.useState<ArticleSaleListItem | null>(null);
  const [editTarget, setEditTarget] = React.useState<ArticleSaleListItem | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const useStackedToolbar = screens.sm === false;

  const setShowHistoricalAndReload = React.useCallback(
    (value: boolean) => {
      setShowHistoricalArticleSales(value);
      window.setTimeout(() => void actionRef.current?.revalidateCurrentQuery(), 0);
    },
    [setShowHistoricalArticleSales]
  );

  const statusLabel = React.useCallback(
    (status: number, submittedSepaDirectDebitRunId?: string | null, paidAmount = 0, openAmount = 0) => {
      const displayStatus = resolveArticleSaleDisplayStatus(status, submittedSepaDirectDebitRunId, paidAmount, openAmount);
      if (displayStatus === "submitted") return text("Direct debit submitted");
      if (displayStatus === "partiallyPaid") return text("Partially paid");
      if (displayStatus === "open") return text("Article sale status open");
      if (displayStatus === "paid") return text("Article sale status paid");
      if (displayStatus === "cancelled") return text("Article sale status cancelled");
      return String(status);
    },
    [text]
  );

  const columns: Record<string, ListColumn<ArticleSaleListItem>> = React.useMemo(
    () => ({
      saleDate: {
        title: text("Sale date"),
        dataIndex: "saleDate",
        key: "saleDate",
        width: SALE_DATE_COLUMN_WIDTH,
        sorter: (a, b) => String(a.saleDate || "").localeCompare(String(b.saleDate || "")),
        render: (v: unknown, r: ArticleSaleListItem) => {
          const raw = String(r.saleDate || v || "").slice(0, ISO_DATE_LENGTH);
          const date = parseDateOnly(raw);
          return date ? formatDateOnly(raw, units) : text("—");
        },
      },
      articleNames: {
        title: text("Article"),
        dataIndex: "articleNames",
        key: "articleNames",
        ellipsis: false,
        sorter: (a, b) => String(a.articleNames || "").localeCompare(String(b.articleNames || "")),
        // Gelöschte Verkäufe liefert das Backend nicht mehr aus; sichtbar bleibt nur der Storno eines
        // bereits bezahlten Verkaufs — der muss ohne zusätzliche Spalte erkennbar sein.
        render: (_: unknown, r: ArticleSaleListItem) => {
          const names = r.articleNames || text("—");
          if (Number(r.status) !== ARTICLE_SALE_STATUS_CANCELLED) return names;
          return (
            <>
              {names}{" "}
              <Tag color="default">{text("Article sale status cancelled")}</Tag>
            </>
          );
        },
      },
      totalGrossAmount: {
        title: text("Price"),
        dataIndex: "totalGrossAmount",
        key: "totalGrossAmount",
        align: "right",
        width: TOTAL_GROSS_AMOUNT_COLUMN_WIDTH,
        sorter: (a, b) => Number(a.totalGrossAmount || 0) - Number(b.totalGrossAmount || 0),
        render: (_: unknown, r: ArticleSaleListItem) => formatMoney(Number(r.totalGrossAmount || 0), units),
      },
      paidAmount: {
        title: text("Paid (status)"),
        dataIndex: "paidAmount",
        key: "paidAmount",
        align: "right",
        width: PAID_AMOUNT_COLUMN_WIDTH,
        sorter: (a, b) => Number(a.paidAmount || 0) - Number(b.paidAmount || 0),
        render: (_: unknown, r: ArticleSaleListItem) => formatMoney(Number(r.paidAmount || 0), units),
      },
      openAmount: {
        title: text("Open (status)"),
        dataIndex: "openAmount",
        key: "openAmount",
        align: "right",
        width: OPEN_AMOUNT_COLUMN_WIDTH,
        sorter: (a, b) => Number(a.openAmount || 0) - Number(b.openAmount || 0),
        render: (_: unknown, r: ArticleSaleListItem) => formatMoney(Number(r.openAmount || 0), units),
      },
      paymentMethod: {
        title: text("Payment method"),
        dataIndex: "paymentMethod",
        key: "paymentMethod",
        width: PAYMENT_METHOD_COLUMN_WIDTH,
        sorter: (a, b) => {
          const la = a.paymentMethod == null ? "" : paymentMethodLabel(Number(a.paymentMethod || 0), paymentMethodNameByType);
          const lb = b.paymentMethod == null ? "" : paymentMethodLabel(Number(b.paymentMethod || 0), paymentMethodNameByType);
          return la.localeCompare(lb);
        },
        render: (_: unknown, r: ArticleSaleListItem) => {
          if (r.paymentMethod == null) return text("—");
          return paymentMethodLabel(Number(r.paymentMethod || 0), paymentMethodNameByType);
        },
      },
      paymentDates: {
        title: text("Payment date"),
        dataIndex: "paymentDates",
        key: "paymentDates",
        width: PAYMENT_DATES_COLUMN_WIDTH,
        sorter: (a, b) => String((a.paymentDates || [])[0] || "").localeCompare(String((b.paymentDates || [])[0] || "")),
        render: (_: unknown, r: ArticleSaleListItem) => {
          const dates = (r.paymentDates || []).map((d) => String(d || "").slice(0, ISO_DATE_LENGTH)).filter(Boolean);
          return formatDateList(dates, units, text("—"));
        },
      },
      status: {
        title: text("Status"),
        dataIndex: "status",
        key: "status",
        width: STATUS_COLUMN_WIDTH,
        sorter: (a, b) => (a.status || 0) - (b.status || 0),
        render: (v: unknown, r: ArticleSaleListItem) => {
          const n = Number(r.status ?? v ?? 0);
          const paidAmount = Number(r.paidAmount || 0);
          const openAmount = Number(r.openAmount || 0);
          const displayStatus = resolveArticleSaleDisplayStatus(n, r.submittedSepaDirectDebitRunId, paidAmount, openAmount);
          if (displayStatus === "submitted") return <Tag color="purple">{statusLabel(n, r.submittedSepaDirectDebitRunId)}</Tag>;
          if (displayStatus === "partiallyPaid") return (
            <>
              <Tag color="gold">{statusLabel(n, r.submittedSepaDirectDebitRunId, paidAmount, openAmount)}</Tag>
              {r.submittedSepaDirectDebitRunId ? <Tag color="purple">{text("Direct debit submitted")}</Tag> : null}
            </>
          );
          if (displayStatus === "open") return <Tag color="orange">{statusLabel(n, r.submittedSepaDirectDebitRunId)}</Tag>;
          if (displayStatus === "paid") return <Tag color="green">{statusLabel(n, r.submittedSepaDirectDebitRunId)}</Tag>;
          if (displayStatus === "cancelled") return <Tag color="default">{statusLabel(n, r.submittedSepaDirectDebitRunId)}</Tag>;
          return <Tag>{statusLabel(Number.isFinite(n) ? n : 0, r.submittedSepaDirectDebitRunId)}</Tag>;
        },
      },
      receiptNumber: {
        title: text("Receipt"),
        dataIndex: "receiptNumber",
        key: "receiptNumber",
        width: RECEIPT_NUMBER_COLUMN_WIDTH,
        sorter: (a, b) => String(a.receiptNumber || "").localeCompare(String(b.receiptNumber || "")),
        render: (_: unknown, r: ArticleSaleListItem) => r.receiptNumber || text("—"),
      },
    }),
    [paymentMethodNameByType, statusLabel, text, units]
  );

  const columnDefinitions: ColumnDefinition[] = React.useMemo(
    () => [
      { key: "saleDate", defaultVisible: true },
      { key: "articleNames", defaultVisible: true },
      { key: "totalGrossAmount", defaultVisible: true },
      { key: "paidAmount", defaultVisible: true },
      { key: "openAmount", defaultVisible: true },
      { key: "paymentMethod", defaultVisible: true },
      { key: "paymentDates", defaultVisible: true },
      { key: "status", defaultVisible: false },
      { key: "receiptNumber", defaultVisible: true },
    ],
    []
  );

  const requestSales = React.useCallback(async () => {
    const q = new URLSearchParams({
      personId: String(personId),
    });
    if (!showHistoricalArticleSales) {
      q.set("startDate", dayjs().startOf("month").format("YYYY-MM-DD"));
    }
    const res = await api
      .get<ArticleSalesListResponse>(`/api/v1/article-sales/list?${q.toString()}`);
    const list = res?.items || [];
    return { data: list, success: true, total: list.length };
  }, [api, personId, showHistoricalArticleSales]);

  if (!canRead) {
    return (
      <div>
        <Typography.Text type="danger">{text("No permission.")}</Typography.Text>
      </div>
    );
  }

  return (
    <>
      <SettingsListPage<ArticleSaleListItem>
        dataMode="complete-scope"
        toolbarExtra={useStackedToolbar ? undefined : (
          <Flex className="person-detail-toolbar-switch" justify="flex-start" align="center" gap={8} style={{ minWidth: 0, width: isMobile ? "100%" : undefined }}>
            <Typography.Text style={{ minWidth: 0 }}>{text("Historical article sales")}</Typography.Text>
            <Switch checked={showHistoricalArticleSales} onChange={setShowHistoricalAndReload} />
          </Flex>
        )}
        toolbarRightExtra={(useStackedToolbar || canVoid) ? (
          <Flex
            wrap="wrap"
            gap={8}
            justify="flex-end"
            align={isMobile ? "stretch" : "center"}
            style={{ width: isMobile ? "100%" : undefined, minWidth: 0 }}
          >
            {useStackedToolbar ? (
              <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
                {text("Filter")}
              </Button>
            ) : null}
            {canVoid ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  return setSellDrawerOpen(true);
                }}
                style={{ whiteSpace: "nowrap" }}
              >
                {text("Sell articles")}
              </Button>
            ) : null}
          </Flex>
        ) : undefined}
        storageKey="persons.detail.articleSales.v3"
        rowKey="id"
        actionRef={actionRef}
        request={requestSales}
        columnDefinitions={columnDefinitions}
        allColumnsMap={columns}
        actionsColumn={
          canVoid || canPay
            ? {
                title: "",
                key: "actions",
                valueType: "option",
              }
            : undefined
        }
        actionSlots={
          canVoid || canPay
            ? [
                {
                  show: (r: ArticleSaleListItem) =>
                    canPay
                    && !r.isPaymentBlockedByActiveSepaRun
                    && Boolean(r.openItemId)
                    && Number(r.openAmount || 0) > OPEN_AMOUNT_ROUNDING_TOLERANCE,
                  render: (r: ArticleSaleListItem) => (
                    <Button type="link" size="small" icon={<CreditCardOutlined />} onClick={() => setPaymentTarget(r)}>
                      {text("Record payment")}
                    </Button>
                  )
                },
                {
                  show: (r: ArticleSaleListItem) => (canVoid || canPay) && Boolean(r.openItemId) && r.status !== ARTICLE_SALE_STATUS_CANCELLED,
                  render: (r: ArticleSaleListItem) => (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        return setEditTarget(r);
                      }}
                    >
                      {text("Edit")}
                    </Button>
                  ),
                },
              ]
            : undefined
        }
      />

      <FormDrawer dirty={false} hideCancel
        title="Filter"
        open={filterDrawerOpen}
        onCancel={() => setFilterDrawerOpen(false)}
        hideSubmit
        cancelText="Close"
        autoFocusFirstField={false}
      >
        <Flex className="person-detail-toolbar-switch" justify="space-between" align="center" gap={8} style={{ minWidth: 0, width: "100%" }}>
          <Typography.Text style={{ minWidth: 0 }}>{text("Historical article sales")}</Typography.Text>
          <Switch checked={showHistoricalArticleSales} onChange={setShowHistoricalAndReload} />
        </Flex>
      </FormDrawer>

      <PersonArticleSaleDrawer
        open={sellDrawerOpen}
        api={api}
        message={message}
        text={text}
        units={units}
        personId={personId}
        homeStudioId={homeStudioId}
        allowedStudios={allowedStudios}
        dateInputFormat={dateInputFormat}
        decimalSeparator={decimalSeparator}
        currencyCode={currencyCode}
        moneyPlaceholder={moneyPlaceholder}
        onClose={() => setSellDrawerOpen(false)}
        onPersonUpdated={onPersonUpdated}
        onSaved={() => void actionRef.current?.revalidateCurrentQuery()}
      />

      {paymentTarget?.openItemId ? (
        <PaymentDrawer
          open
          title={text("Record payment")}
          onClose={() => setPaymentTarget(null)}
          onSuccess={() => {
            setPaymentTarget(null);
            void actionRef.current?.revalidateCurrentQuery();
            onPaymentRecorded();
          }}
          items={[{
            id: paymentTarget.openItemId,
            description: paymentTarget.articleNames || text("Article"),
            dueDate: paymentTarget.saleDate,
            openAmount: Number(paymentTarget.openAmount || 0),
            paymentMethod: paymentTarget.paymentMethod,
            type: "openItem"
          } as PaymentItem]}
          selectedItems={[{
            id: paymentTarget.openItemId,
            description: paymentTarget.articleNames || text("Article"),
            dueDate: paymentTarget.saleDate,
            openAmount: Number(paymentTarget.openAmount || 0),
            paymentMethod: paymentTarget.paymentMethod,
            type: "openItem"
          } as PaymentItem]}
          onSelectedItemsChange={() => {}}
          paymentEndpoint={(id) => `/api/v1/open-items/${encodeURIComponent(id)}/pay`}
          itemType="openItem"
          personId={personId}
        />
      ) : null}

      <ArticleSaleEditDrawer
        target={editTarget}
        api={api}
        message={message}
        text={text}
        canEditPayment={canPay}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          void actionRef.current?.revalidateCurrentQuery();
          onPaymentRecorded();
        }}
      />
    </>
  );
}
